import React, { useReducer, useCallback } from 'react';
import { Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { NegativePromptSectionMemo } from './NegativePromptSection';
import type { IVerticalPromptSectionProps } from '@/@types/propertySceneCard';

function getEffectiveImagePrompt(
  scene: IVerticalPromptSectionProps['scene'],
  template: IVerticalPromptSectionProps['template']
): string {
  if (scene.editedImagePrompt) return scene.editedImagePrompt;
  if (template) return template.positivePrompt;
  return scene.imageToVideoPrompt ?? '';
}

function getEffectiveNegativePrompt(
  scene: IVerticalPromptSectionProps['scene'],
  template: IVerticalPromptSectionProps['template']
): string {
  const DEFAULT_NEGATIVE = 'blurry, distorted, low quality, amateur, shaky camera';
  if (scene.editedNegativePrompt) return scene.editedNegativePrompt;
  if (template) return template.negativePrompt;
  return DEFAULT_NEGATIVE;
}

interface IPromptState {
  localPositivePrompt: string;
  localNegativePrompt: string;
  isPromptDirty: boolean;
  prevPositivePrompt: string;
  prevNegativePrompt: string;
}

type PromptAction =
  | { type: 'SET_POSITIVE_PROMPT'; value: string }
  | { type: 'SET_NEGATIVE_PROMPT'; value: string }
  | { type: 'MARK_SAVED' }
  | { type: 'SYNC_FROM_PROPS'; positivePrompt: string; negativePrompt: string };

function promptReducer(state: IPromptState, action: PromptAction): IPromptState {
  switch (action.type) {
    case 'SET_POSITIVE_PROMPT':
      return { ...state, localPositivePrompt: action.value, isPromptDirty: true };
    case 'SET_NEGATIVE_PROMPT':
      return { ...state, localNegativePrompt: action.value, isPromptDirty: true };
    case 'MARK_SAVED':
      return { ...state, isPromptDirty: false };
    case 'SYNC_FROM_PROPS':
      return {
        localPositivePrompt: action.positivePrompt,
        localNegativePrompt: action.negativePrompt,
        isPromptDirty: false,
        prevPositivePrompt: action.positivePrompt,
        prevNegativePrompt: action.negativePrompt,
      };
  }
}

export function VerticalPromptSection({
  sceneId,
  scene,
  template,
  isSaving,
  onPromptEdit,
  onImagePromptChange,
  onNegativePromptChange,
}: IVerticalPromptSectionProps) {
  const effectivePositivePrompt = getEffectiveImagePrompt(scene, template);
  const effectiveNegativePrompt = getEffectiveNegativePrompt(scene, template);

  const [state, dispatch] = useReducer(promptReducer, {
    localPositivePrompt: effectivePositivePrompt,
    localNegativePrompt: effectiveNegativePrompt,
    isPromptDirty: false,
    prevPositivePrompt: effectivePositivePrompt,
    prevNegativePrompt: effectiveNegativePrompt,
  });

  if (
    effectivePositivePrompt !== state.prevPositivePrompt ||
    effectiveNegativePrompt !== state.prevNegativePrompt
  ) {
    dispatch({
      type: 'SYNC_FROM_PROPS',
      positivePrompt: effectivePositivePrompt,
      negativePrompt: effectiveNegativePrompt,
    });
  }

  const handlePositivePromptChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    dispatch({ type: 'SET_POSITIVE_PROMPT', value: e.target.value });
  }, []);

  const handleNegativePromptChange = useCallback((value: string) => {
    dispatch({ type: 'SET_NEGATIVE_PROMPT', value });
  }, []);

  const handleSavePrompts = useCallback(async () => {
    if (!state.isPromptDirty) return;

    if (state.localPositivePrompt !== effectivePositivePrompt) {
      await onImagePromptChange(state.localPositivePrompt);
    }
    if (state.localNegativePrompt !== effectiveNegativePrompt) {
      onNegativePromptChange?.(sceneId, state.localNegativePrompt);
    }
    dispatch({ type: 'MARK_SAVED' });
  }, [
    state.isPromptDirty,
    state.localPositivePrompt,
    state.localNegativePrompt,
    effectivePositivePrompt,
    effectiveNegativePrompt,
    onImagePromptChange,
    onNegativePromptChange,
    sceneId,
  ]);

  return (
    <>
      <div className="border-t pt-4">
        <div className="flex items-center justify-between mb-2">
          <label htmlFor={`scene-i2v-prompt-${sceneId}`} className="text-sm font-medium">
            Image-to-Video Prompt:
          </label>
          <div className="flex items-center gap-2">
            {state.isPromptDirty && (
              <Button size="sm" variant="outline" onClick={handleSavePrompts} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Prompts'}
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={onPromptEdit}
              aria-label="Edit prompt with AI assistance"
            >
              <Sparkles className="h-4 w-4 mr-1" />
              AI
            </Button>
          </div>
        </div>
        <Textarea
          id={`scene-i2v-prompt-${sceneId}`}
          value={state.localPositivePrompt}
          onChange={handlePositivePromptChange}
          rows={4}
          className="resize-none"
          placeholder="Image-to-video prompt..."
        />
      </div>

      <NegativePromptSectionMemo
        value={state.localNegativePrompt}
        defaultValue={effectiveNegativePrompt}
        onChange={handleNegativePromptChange}
        disabled={isSaving}
      />
    </>
  );
}
